import { useCallback, useEffect, useRef, useState } from "react";
import { UploadService } from "../../../../services/uploadService";
import {
  MAX_CLUB_GALLERY_PHOTOS,
  getClubGalleryPhotoCount,
} from "../../../../components/admin/editBioHelpers";
import {
  applySettledUploadResults,
  cancelPhotoSession,
  createPhotoSessionId,
  getSessionUploadsForCleanup,
  removeStagedPhotoAsset,
  stagePhotoAssets,
} from "../../../../utils/adminPhotoHandoff";
import { compressForUpload, pickFiles, releaseUri } from "../../lib/files";
import { msg } from "./manageUtils";

async function imageSize(file) {
  try {
    const bmp = await createImageBitmap(file);
    const out = { width: bmp.width, height: bmp.height };
    bmp.close?.();
    return out;
  } catch {
    return {};
  }
}

/**
 * The staged-photo state machine from AdminScreen (stagedPhotoAssets, photoSessionId,
 * uploadingGallery, cleanupRetryAssets; pickGalleryImages :1413-1502, cancelPhotoHandoff :1559).
 * Photos are uploaded immediately after staging; handleSaveBio re-attempts selected/failed ones.
 * One instance per club (ClubManager is keyed by club id), so a club switch or leaving /manage
 * unmounts the whole session: unsaved uploads are deleted then, unless a save is in flight (saveBio
 * then cleans up against the URLs it saved). An upload that resolves after the session ended
 * deletes its own result.
 */
export function useBioPhotos({ uid, clubId, savedGallery, notify }) {
  const [staged, setStaged] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cleanupRetry, setCleanupRetry] = useState([]);

  const stagedRef = useRef(staged);
  const sessionRef = useRef(sessionId);
  const retryRef = useRef(cleanupRetry);
  const galleryRef = useRef(savedGallery);
  const sessionAlive = useRef(false); // true between effect setup and cleanup (StrictMode safe)
  const savingRef = useRef(false); // set by an in-flight Edit Bio save; saveBio owns cleanup then
  const busyRef = useRef(false); // synchronous lock: one pick/compress/upload at a time
  stagedRef.current = staged;
  sessionRef.current = sessionId;
  retryRef.current = cleanupRetry;
  galleryRef.current = savedGallery;

  const updateStaged = useCallback((fn) => {
    const next = typeof fn === "function" ? fn(stagedRef.current) : fn;
    stagedRef.current = next;
    setStaged(next);
  }, []);

  /** Drop the whole session locally (no Storage cleanup). */
  const reset = useCallback(() => {
    stagedRef.current.forEach((a) => releaseUri(a.uploadUri));
    stagedRef.current = [];
    sessionRef.current = null;
    setStaged([]);
    setSessionId(null);
  }, []);

  /** Cancel: mark session cancelled, delete unsaved uploads, reset (cancelPhotoHandoff). */
  const cancel = useCallback(async () => {
    const cancelled = cancelPhotoSession(stagedRef.current);
    const uploads = getSessionUploadsForCleanup(cancelled);
    reset();
    if (uploads.length > 0 && clubId && uid) {
      const { data } = await UploadService.cleanupUnsavedClubGalleryUploads({
        clubId,
        uid,
        uploads: [...retryRef.current, ...uploads],
        savedGalleryUrls: Array.isArray(galleryRef.current) ? galleryRef.current : [],
      });
      setCleanupRetry(data?.failed || []);
    }
  }, [uid, clubId, reset]);

  // Unmounting (club switch / leaving the page) abandons the session like Cancel does, unless a save is in flight.
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;
  useEffect(() => {
    sessionAlive.current = true;
    return () => {
      sessionAlive.current = false;
      // Intentionally read at unmount time: a save in flight owns the cleanup.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (!savingRef.current) cancelRef.current();
    };
  }, []);

  const remove = useCallback((assetIdentity) => {
    updateStaged((prev) => removeStagedPhotoAsset(prev, assetIdentity));
  }, [updateStaged]);

  /** Stage + immediately upload picked image Files. Resolves true when something was staged. */
  const stageAndUpload = useCallback(async (files, currentSaved) => {
    const cid = clubId;
    const list = Array.from(files || []).filter((f) => /^image\//.test(f.type));
    if (!list.length || !cid || !uid) return false;
    const saved = Array.isArray(currentSaved) ? currentSaved : [];
    const remaining = MAX_CLUB_GALLERY_PHOTOS - getClubGalleryPhotoCount(saved, stagedRef.current);
    if (remaining <= 0) {
      notify(`Gallery full: you can upload up to ${MAX_CLUB_GALLERY_PHOTOS} photos. Remove one to add another.`, "alert");
      return false;
    }
    const picked = list.slice(0, remaining);
    setUploading(true);
    try {
      const settled = await Promise.allSettled(
        picked.map(async (file) => {
          const [uploadUri, size] = await Promise.all([compressForUpload(file), imageSize(file)]);
          return {
            // Identity from the file itself (mobile uses the picker asset id) so re-picking the same file in a session dedupes.
            assetId: `${file.name}:${file.size}:${file.lastModified}`,
            uri: uploadUri,
            sourceUri: uploadUri,
            uploadUri,
            fileName: file.name,
            ...size,
          };
        })
      );
      const assets = settled.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const bad = settled.find((r) => r.status === "rejected");
      if (bad) notify(msg(bad.reason, "Failed to process a photo"), "alert");
      // Re-check capacity now that compression is done.
      const room = Math.max(0, MAX_CLUB_GALLERY_PHOTOS - getClubGalleryPhotoCount(saved, stagedRef.current));
      assets.slice(room).forEach((a) => releaseUri(a.uploadUri));
      assets.length = Math.min(assets.length, room);
      if (!assets.length || !sessionAlive.current) {
        assets.forEach((a) => releaseUri(a.uploadUri));
        return false;
      }
      const sid = sessionRef.current || createPhotoSessionId();
      sessionRef.current = sid;
      setSessionId(sid);
      const fresh = stagePhotoAssets({ uid, clubId: cid, sessionId: sid, assets });
      const known = new Set(stagedRef.current.map((a) => a.assetIdentity));
      const added = fresh.filter((a) => !known.has(a.assetIdentity));
      assets.forEach((a) => { if (!added.some((s) => s.uploadUri === a.uploadUri)) releaseUri(a.uploadUri); });
      updateStaged((prev) => [...prev, ...added]);
      // Upload right away (KAN-81); deterministic object keys make a repeat safe.
      if (!added.length) return true;
      const { data, error } = await UploadService.uploadClubGalleryImagesSettled(cid, added);
      if (!error) {
        const results = [...(data?.uploaded || []), ...(data?.failed || [])];
        if (sessionAlive.current && sessionRef.current === sid) {
          updateStaged((prev) => applySettledUploadResults(prev, results));
        } else {
          // The session ended while uploading: delete what just landed so nothing is left unreferenced.
          const uploads = getSessionUploadsForCleanup(applySettledUploadResults(added, results));
          if (uploads.length > 0) {
            UploadService.cleanupUnsavedClubGalleryUploads({
              clubId: cid,
              uid,
              uploads,
              savedGalleryUrls: Array.isArray(galleryRef.current) ? galleryRef.current : [],
            });
          }
        }
      }
      return true;
    } catch (e) {
      notify(msg(e, "Failed to add photos"), "alert");
      return false;
    } finally {
      setUploading(false);
    }
  }, [uid, clubId, notify, updateStaged]);

  const addPhotos = useCallback(async (files, currentSaved) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    try { return await stageAndUpload(files, currentSaved); } finally { busyRef.current = false; }
  }, [stageAndUpload]);

  const pickPhotos = useCallback(async (currentSaved) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    try {
      const files = await pickFiles({ accept: "image/*", multiple: true });
      return await stageAndUpload(files, currentSaved);
    } finally { busyRef.current = false; }
  }, [stageAndUpload]);

  return {
    staged, uploading, setUploading, cleanupRetry, setCleanupRetry,
    stagedRef, retryRef, savingRef, updateStaged, reset, cancel, remove, addPhotos, pickPhotos,
  };
}
